import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

interface BentoItemProps {
  className?: string;
  title: string;
  description?: string;
  header?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

interface BentoGridProps {
  children: React.ReactNode;
  className?: string;
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[6]};
  max-width: 100%;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: ${theme.spacing[4]};
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
  }
`;

const BentoCard = styled.div`
  group relative col-span-3 flex flex-col justify-between overflow-hidden rounded-xl;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  position: relative;
  overflow: hidden;
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.sm};

  &:hover {
    border-color: ${theme.colors.primary[300]};
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${theme.colors.primary[400]}, transparent);
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }

  &:hover::before {
    opacity: 1;
  }

  /* Special grid layouts */
  &.col-span-1 {
    grid-column: span 1;
  }
  
  &.col-span-2 {
    grid-column: span 2;
  }
  
  &.col-span-3 {
    grid-column: span 3;
  }
  
  &.row-span-1 {
    grid-row: span 1;
  }
  
  &.row-span-2 {
    grid-row: span 2;
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    &.col-span-2,
    &.col-span-3 {
      grid-column: span 1;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[600]};
  font-size: 20px;
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing[4]} 0;
  line-height: 1.5;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardHeaderContainer = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

export const BentoGrid: React.FC<BentoGridProps> = ({ children, className }) => {
  return (
    <GridContainer className={className}>
      {children}
    </GridContainer>
  );
};

export const BentoGridItem: React.FC<BentoItemProps> = ({
  className,
  title,
  description,
  header,
  icon,
  children
}) => {
  return (
    <BentoCard className={className}>
      {header && (
        <CardHeaderContainer>
          {header}
        </CardHeaderContainer>
      )}
      
      <CardHeader>
        {icon && <CardIcon>{icon}</CardIcon>}
        <div>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
      </CardHeader>
      
      <CardContent>
        {children}
      </CardContent>
    </BentoCard>
  );
};